import * as React from 'react';
import { bindActionCreators, Dispatch } from 'redux';

import {
  setCompanyNameFieldValueAction,
  setCompanyTypeFieldValueAction,
  setEmailFieldValueAction,
  setPhoneFieldValueAction,
  validateAllAction,
  validateByFieldAction,
} from './internal/actions';
import { PlacementApplicationFormView } from './internal/components/PlacementApplicationFormView';
import { validationRulesEntries } from './internal/utils';
import {
  getPlacementApplicationFormInitialState,
  placementApplicationFormReducer,
} from './internal/reducer';
import {
  CheckFormFieldInvalid,
  GetFormFieldErrorMessage,
  GetFormFieldValue,
  HandleBlur,
  HandleSubmit,
  ICoworkingPlacementApplicationFormProps,
} from './types';

export const CoworkingPlacementApplicationForm: React.FC<ICoworkingPlacementApplicationFormProps> = props => {
  const { isDisableFieldsOnPending = false, formInitialState, onSubmit, pending = false } = props;

  const [formState, formDispatch] = React.useReducer(
    placementApplicationFormReducer,
    getPlacementApplicationFormInitialState(formInitialState),
  );

  const boundActions = React.useMemo(
    () =>
      bindActionCreators(
        {
          setCompanyNameFieldValueAction,
          setCompanyTypeFieldValueAction,
          setEmailFieldValueAction,
          setPhoneFieldValueAction,
          validateAllAction,
          validateByFieldAction,
        },
        formDispatch as Dispatch,
      ),
    [],
  );

  const handleSubmit = React.useCallback<HandleSubmit>(
    event => {
      event.preventDefault();

      const formValuesValid = !validationRulesEntries.some(
        ([ruleKey, ruleFunc]) => ruleFunc(formState[ruleKey].value, formState),
      );

      if (formValuesValid) {
        onSubmit(formState);
      } else {
        boundActions.validateAllAction();
      }
    },
    [formState, onSubmit, boundActions],
  );

  const handleBlur: HandleBlur = fieldName => () => boundActions.validateByFieldAction(fieldName);

  const getFormFieldErrorMessage = React.useCallback<GetFormFieldErrorMessage>(
    fieldType => formState[fieldType].errorMessage,
    [formState],
  );

  const checkFormFieldInvalid = React.useCallback<CheckFormFieldInvalid>(type => !!getFormFieldErrorMessage(type), [
    getFormFieldErrorMessage,
  ]);

  const getFormFieldValue: GetFormFieldValue = fieldType => formState[fieldType].value;

  return (
    <PlacementApplicationFormView
      boundActions={boundActions}
      checkFormFieldInvalid={checkFormFieldInvalid}
      isDisableFieldsOnPending={isDisableFieldsOnPending}
      getFormFieldErrorMessage={getFormFieldErrorMessage}
      getFormFieldValue={getFormFieldValue}
      onBlur={handleBlur}
      onSubmit={handleSubmit}
      pending={pending}
    />
  );
};

CoworkingPlacementApplicationForm.displayName = 'CoworkingPlacementApplicationForm';
